import { useEffect, useState } from "react";
import { useParams, Link, useLocation } from "wouter";
import { NurseLayout } from "@/components/layout/nurse-layout";
import { PharmacistLayout } from "@/components/layout/pharmacist-layout";
import { DoctorLayout } from "@/components/layout/doctor-layout";
import { Button } from "@/components/ui/button";
import {
  Loader2, ArrowLeft, User, Calendar, Building2, Pill, AlertTriangle,
  CheckCircle2, Clock, Inbox, Activity, FileText, ClipboardPen,
  Sparkles, MessageSquare, ChevronRight,
} from "lucide-react";

const BASE_URL = import.meta.env.BASE_URL.replace(/\/$/, "");

type Severity = "red" | "amber" | "green" | "info";
type MonitoringStatus = "red" | "amber" | "green" | "none";
type Role = "nurse" | "pharmacist" | "doctor";

interface ActiveMedication {
  id: number;
  medicationName: string;
  medicationType: string | null;
  dose: string | null;
  frequency: string | null;
  supplier: string | null;
  lastPrescribedAt: string | null;
  nextDueAt: string;
  daysUntilDue: number;
  status: "red" | "amber" | "green";
  isRecentChange: boolean;
  notes: string | null;
}

interface MonitoringItem {
  kind: "medication" | "blood-monitoring" | "request";
  label: string;
  dueDate: string | null;
  daysUntilDue: number | null;
  severity: Severity;
}

interface PatientRequest {
  id: number;
  requestType: string;
  patientName: string;
  patientDob: string | null;
  medicationName: string | null;
  queryDetails: string | null;
  testType: string | null;
  testNotes: string | null;
  createdAt: string;
}

interface Summary {
  view: "clinical" | "coordinator";
  generatedAt: string;
  patient: {
    id: number;
    rheumHubId: string;
    firstName: string;
    lastName: string;
    dateOfBirth: string;
    hospitalNumber: string;
    organisationName: string;
    diagnosis: string | null;
    notes: string | null;
  };
  activeMedications: ActiveMedication[];
  monitoring: {
    status: MonitoringStatus;
    overdueCount: number;
    dueSoonCount: number;
    alertDaysAdvance: number;
    items: MonitoringItem[];
  };
  outstandingRequests: {
    items: PatientRequest[];
    totalCount: number;
    matchedBy: "name-dob" | "name-only" | "nhs-number" | "none";
  };
  diseaseActivity: {
    available: boolean;
    scores: { type: string; value: number; recordedAt: string }[];
  };
  lastConsultation: {
    available: boolean;
    date: string | null;
    clinician: string | null;
    planSummary: string | null;
  };
  nextReview: {
    available: boolean;
    date: string | null;
  };
}

function formatDate(d: string): string {
  return new Date(d.length === 10 ? d + "T00:00:00" : d).toLocaleDateString("en-GB", {
    day: "numeric", month: "short", year: "numeric",
  });
}

function calcAge(dob: string): number {
  const b = new Date(dob);
  const t = new Date();
  let age = t.getFullYear() - b.getFullYear();
  const m = t.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && t.getDate() < b.getDate())) age--;
  return age;
}

const SEVERITY_BADGE: Record<Severity, string> = {
  red: "bg-red-100 text-red-700 border-red-200",
  amber: "bg-amber-100 text-amber-800 border-amber-200",
  green: "bg-green-100 text-green-700 border-green-200",
  info: "bg-sky-100 text-sky-700 border-sky-200",
};

const SEVERITY_ICON: Record<Severity, React.ReactNode> = {
  red: <AlertTriangle className="h-4 w-4" />,
  amber: <Clock className="h-4 w-4" />,
  green: <CheckCircle2 className="h-4 w-4" />,
  info: <Inbox className="h-4 w-4" />,
};

const STATUS_BANNER: Record<MonitoringStatus, { cls: string; label: string }> = {
  red: { cls: "bg-red-50 border-red-200 text-red-800", label: "Action needed before consultation" },
  amber: { cls: "bg-amber-50 border-amber-200 text-amber-800", label: "Items due soon — review with patient" },
  green: { cls: "bg-green-50 border-green-200 text-green-800", label: "On track" },
  none: { cls: "bg-slate-50 border-slate-200 text-slate-700", label: "No active monitoring items" },
};

function SectionCard({
  title, icon, action, children, subtitle,
}: {
  title: string;
  icon: React.ReactNode;
  action?: React.ReactNode;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
      <header className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="text-teal-700 shrink-0">{icon}</div>
          <div className="min-w-0">
            <h2 className="font-semibold text-slate-900 text-sm">{title}</h2>
            {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
          </div>
        </div>
        {action}
      </header>
      <div className="p-5">{children}</div>
    </section>
  );
}

function Sparkline({ values }: { values: number[] }) {
  if (values.length === 0) return null;
  const W = 120, H = 32, PAD = 2;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const points = values.map((v, i) => {
    const x = PAD + (i * (W - PAD * 2)) / Math.max(values.length - 1, 1);
    const y = H - PAD - ((v - min) / range) * (H - PAD * 2);
    return `${x},${y}`;
  }).join(" ");
  return (
    <svg width={W} height={H} className="text-teal-600" aria-hidden="true">
      <polyline points={points} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

export default function PatientPreConsultSummaryPage() {
  const params = useParams<{ id: string }>();
  const patientId = parseInt(params.id ?? "", 10);
  const [, navigate] = useLocation();
  const [summary, setSummary] = useState<Summary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const role = (localStorage.getItem("rheumhub_role") as Role) ?? "nurse";

  useEffect(() => {
    if (isNaN(patientId)) {
      setError("Invalid patient ID");
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${BASE_URL}/api/med-tracker/patients/${patientId}/summary`, {
          credentials: "include",
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          if (!cancelled) setError(body?.error ?? `Failed to load summary (${res.status})`);
          return;
        }
        const data = (await res.json()) as Summary;
        if (!cancelled) setSummary(data);
      } catch {
        if (!cancelled) setError("Network error while loading summary");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [patientId]);

  const handleStartConsultationNote = () => {
    // Consultation outcome recording feature is not yet implemented (see task dependency).
    // For now, return to the patient detail panel so the clinician can record outcomes there.
    navigate("/med-tracker");
  };

  const content = (
    <div className="max-w-5xl mx-auto space-y-4 pb-12">
      {/* Back nav */}
      <div className="flex items-center justify-between">
        <Link
          href="/med-tracker"
          className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-teal-700"
        >
          <ArrowLeft className="h-4 w-4" /> Back to patients
        </Link>
        {summary && (
          <span className="text-xs text-slate-400">
            Generated {new Date(summary.generatedAt).toLocaleString("en-GB", { timeZone: "Europe/London" })}
          </span>
        )}
      </div>

      {loading && (
        <div className="flex items-center justify-center py-24 text-slate-400">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      )}

      {!loading && error && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-red-800">
          <p className="font-semibold mb-1">Unable to load summary</p>
          <p className="text-sm">{error}</p>
        </div>
      )}

      {!loading && !error && summary && (
        <>
          {/* Header */}
          <header className="bg-gradient-to-br from-teal-700 to-teal-800 text-white rounded-2xl px-6 py-5">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-wider text-teal-200 font-medium">Pre-consultation summary</p>
                <h1 className="text-2xl font-bold mt-1">
                  {summary.patient.firstName} {summary.patient.lastName}
                </h1>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm text-teal-100">
                  <span className="inline-flex items-center gap-1.5">
                    <Calendar className="h-3.5 w-3.5" />
                    {formatDate(summary.patient.dateOfBirth)} ({calcAge(summary.patient.dateOfBirth)} y)
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <Building2 className="h-3.5 w-3.5" />
                    #{summary.patient.hospitalNumber}
                  </span>
                  {summary.patient.diagnosis && (
                    <span className="inline-flex items-center gap-1.5">
                      <Activity className="h-3.5 w-3.5" />
                      {summary.patient.diagnosis}
                    </span>
                  )}
                </div>
                <p className="text-xs text-teal-200 mt-2">{summary.patient.organisationName}</p>
              </div>
              <div className="flex flex-col items-end gap-1">
                <Button
                  onClick={handleStartConsultationNote}
                  disabled
                  title="Consultation note recording is coming soon"
                  aria-label="Start consultation note (coming soon)"
                  className="bg-white text-teal-800 hover:bg-teal-50 shadow gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <ClipboardPen className="h-4 w-4" /> Start consultation note
                </Button>
                <span className="text-[11px] text-teal-200">Coming soon</span>
              </div>
            </div>
          </header>

          {/* Status banner */}
          <div className={`flex items-start gap-3 rounded-xl px-4 py-3 border ${STATUS_BANNER[summary.monitoring.status].cls}`}>
            <Sparkles className="h-4 w-4 shrink-0 mt-0.5" />
            <div className="text-sm">
              <span className="font-semibold">{STATUS_BANNER[summary.monitoring.status].label}.</span>{" "}
              {summary.monitoring.overdueCount > 0 && (
                <span>{summary.monitoring.overdueCount} overdue. </span>
              )}
              {summary.monitoring.dueSoonCount > 0 && (
                <span>{summary.monitoring.dueSoonCount} due within {summary.monitoring.alertDaysAdvance} days. </span>
              )}
              {summary.outstandingRequests.totalCount > 0 && (
                <span>{summary.outstandingRequests.totalCount} outstanding patient {summary.outstandingRequests.totalCount === 1 ? "query" : "queries"}.</span>
              )}
            </div>
          </div>

          {/* Two-column body */}
          <div className="grid md:grid-cols-2 gap-4">
            {/* Last consultation */}
            <SectionCard title="Last consultation" icon={<FileText className="h-4 w-4" />}>
              {summary.lastConsultation.available ? (
                <div className="space-y-1 text-sm">
                  <p className="font-medium text-slate-900">
                    {summary.lastConsultation.date && formatDate(summary.lastConsultation.date)}
                  </p>
                  <p className="text-slate-600">{summary.lastConsultation.clinician}</p>
                  <p className="text-slate-700 mt-2">{summary.lastConsultation.planSummary}</p>
                </div>
              ) : (
                <p className="text-sm text-slate-500 italic">
                  No consultation history recorded yet. Consultation outcome recording arrives in a separate update.
                </p>
              )}
            </SectionCard>

            {/* Next review */}
            <SectionCard title="Next review" icon={<Calendar className="h-4 w-4" />}>
              {summary.nextReview.available && summary.nextReview.date ? (
                <p className="text-sm font-medium text-slate-900">{formatDate(summary.nextReview.date)}</p>
              ) : (
                <p className="text-sm text-slate-500 italic">
                  Not scheduled — will appear once a consultation record sets a next review date.
                </p>
              )}
            </SectionCard>

            {/* Disease activity */}
            <SectionCard
              title="Disease activity trend"
              icon={<Activity className="h-4 w-4" />}
              subtitle="Last 3 DAS28 / CDAI scores"
            >
              {summary.diseaseActivity.available && summary.diseaseActivity.scores.length > 0 ? (
                <div className="flex items-end justify-between gap-4">
                  <div className="space-y-0.5">
                    {summary.diseaseActivity.scores.slice(0, 3).map((s, i) => (
                      <p key={i} className="text-sm text-slate-700">
                        <span className="font-semibold">{s.type}: {s.value.toFixed(2)}</span>{" "}
                        <span className="text-xs text-slate-500">({formatDate(s.recordedAt)})</span>
                      </p>
                    ))}
                  </div>
                  <Sparkline values={summary.diseaseActivity.scores.map((s) => s.value)} />
                </div>
              ) : (
                <p className="text-sm text-slate-500 italic">
                  No saved disease activity scores yet. Score persistence will arrive alongside consultation recording.
                </p>
              )}
            </SectionCard>

            {/* Outstanding items (full width on the right column wraps) */}
            <SectionCard
              title="Outstanding items"
              icon={<AlertTriangle className="h-4 w-4" />}
              subtitle="Overdue, due soon, and unanswered queries"
            >
              {summary.monitoring.items.length === 0 ? (
                <p className="text-sm text-slate-500 italic">Nothing outstanding. Good to go.</p>
              ) : (
                <ul className="space-y-2">
                  {summary.monitoring.items.map((item, i) => (
                    <li key={i} className={`flex items-start gap-2.5 rounded-xl border px-3 py-2 ${SEVERITY_BADGE[item.severity]}`}>
                      <span className="shrink-0 mt-0.5">{SEVERITY_ICON[item.severity]}</span>
                      <div className="text-sm flex-1 min-w-0">
                        <p className="font-medium">{item.label}</p>
                        {item.dueDate && (
                          <p className="text-xs opacity-80 mt-0.5">Due {formatDate(item.dueDate)}</p>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </SectionCard>
          </div>

          {/* Current medications */}
          <SectionCard
            title="Current medications"
            icon={<Pill className="h-4 w-4" />}
            subtitle="All active prescriptions. Recently changed items are flagged."
            action={
              <Link
                href="/med-tracker"
                className="text-xs text-teal-700 hover:underline inline-flex items-center gap-1"
              >
                Edit in tracker <ChevronRight className="h-3 w-3" />
              </Link>
            }
          >
            {summary.activeMedications.length === 0 ? (
              <p className="text-sm text-slate-500 italic">No active medications on record.</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {summary.activeMedications.map((m) => {
                  const isRed = m.status === "red";
                  const isAmber = m.status === "amber";
                  const dueText = m.daysUntilDue < 0
                    ? `${Math.abs(m.daysUntilDue)} day${Math.abs(m.daysUntilDue) === 1 ? "" : "s"} overdue`
                    : m.daysUntilDue === 0
                      ? "Due today"
                      : `Due in ${m.daysUntilDue} day${m.daysUntilDue === 1 ? "" : "s"}`;
                  return (
                    <li key={m.id} className="py-3 first:pt-0 last:pb-0">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-medium text-slate-900 text-sm flex items-center gap-2 flex-wrap">
                            {m.medicationName}
                            {m.medicationType && (
                              <span className="text-xs text-slate-500 font-normal">{m.medicationType}</span>
                            )}
                            {m.isRecentChange && (
                              <span className="text-[10px] uppercase font-semibold tracking-wide bg-teal-100 text-teal-800 px-1.5 py-0.5 rounded-full">
                                Recent change
                              </span>
                            )}
                          </p>
                          <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-xs text-slate-500">
                            {m.dose && <span>Dose: {m.dose}</span>}
                            {m.frequency && <span>Freq: {m.frequency}</span>}
                            {m.supplier && <span>Supplier: {m.supplier}</span>}
                            {m.lastPrescribedAt && <span>Last prescribed: {formatDate(m.lastPrescribedAt)}</span>}
                          </div>
                          {m.notes && (
                            <p className="mt-1 text-xs italic text-slate-500">{m.notes}</p>
                          )}
                        </div>
                        <span className={`shrink-0 text-xs font-medium rounded-full px-2.5 py-1 border ${
                          isRed ? "bg-red-50 text-red-700 border-red-200"
                            : isAmber ? "bg-amber-50 text-amber-800 border-amber-200"
                              : "bg-green-50 text-green-700 border-green-200"
                        }`}>
                          {dueText}
                        </span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </SectionCard>

          {/* Recent patient activity */}
          <SectionCard
            title="Recent patient activity"
            icon={<MessageSquare className="h-4 w-4" />}
            subtitle={
              summary.outstandingRequests.matchedBy === "name-dob"
                ? "Matched by name and date of birth"
                : summary.outstandingRequests.matchedBy === "name-only"
                  ? "Matched by name only — confirm identity"
                  : "No matching submissions found"
            }
          >
            {summary.outstandingRequests.items.length === 0 ? (
              <p className="text-sm text-slate-500 italic">
                No queries or activity submitted via the patient portal that match this record.
              </p>
            ) : (
              <ul className="space-y-3">
                {summary.outstandingRequests.items.map((r) => (
                  <li key={r.id} className="border border-slate-200 rounded-xl p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-900">
                          {r.requestType === "prescription-query"
                            ? "Prescription query"
                            : r.requestType === "blood-form-request"
                              ? "Blood form request"
                              : r.requestType}
                        </p>
                        {r.medicationName && (
                          <p className="text-xs text-slate-500 mt-0.5">Medication: {r.medicationName}</p>
                        )}
                        {r.testType && (
                          <p className="text-xs text-slate-500 mt-0.5">Test: {r.testType}</p>
                        )}
                        {r.queryDetails && (
                          <p className="text-sm text-slate-700 mt-1.5 line-clamp-3">{r.queryDetails}</p>
                        )}
                        {r.testNotes && (
                          <p className="text-sm text-slate-700 mt-1.5 line-clamp-3">{r.testNotes}</p>
                        )}
                      </div>
                      <span className="shrink-0 text-xs text-slate-400">
                        {new Date(r.createdAt).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </SectionCard>

          {summary.patient.notes && (
            <SectionCard title="Clinical notes" icon={<User className="h-4 w-4" />}>
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{summary.patient.notes}</p>
            </SectionCard>
          )}

          <p className="text-xs text-slate-400 text-center">
            This is a read-only briefing view. Make changes via the medication tracker or the consultation note form.
          </p>
        </>
      )}
    </div>
  );

  const title = "Pre-consultation summary";
  if (role === "doctor") return <DoctorLayout title={title}>{content}</DoctorLayout>;
  if (role === "pharmacist") return <PharmacistLayout title={title}>{content}</PharmacistLayout>;
  return <NurseLayout title={title}>{content}</NurseLayout>;
}
